/**
 * Connector Settings — admin
 *
 * Intervals are system-wide, not per-brand. Mirakl's call-frequency budget belongs to
 * the retailer ACCOUNT: ten brands sharing one Macy's account share one ceiling, so
 * letting each brand pick its own rate is how that account gets rate-limited. That is
 * why there is no interval field on an individual brand-retailer connection.
 *
 * Every option list and ceiling below comes from the server, not from a copy kept here.
 * The order-sweep floor moves with the external-partner flag, so a hardcoded list would
 * offer intervals the save then rejects.
 *
 * ORGANISED BY WHAT AN ADMIN IS DECIDING, not by which API the call belongs to.
 *
 * The screen used to be four equal sections — reads from Mirakl, reads from Shopify, writes
 * to Mirakl, writes to Shopify — fourteen controls, all the same size, with no summary. That
 * is the shape of the code, not of the decision. An admin arrives asking three things: is it
 * running, is it allowed to change anything, and how often does it check. So:
 *
 *   1. A status line answers "is it running" before anything else.
 *   2. The four WRITES keep individual switches, because each one costs something different
 *      when it is off and that consequence sits next to the switch.
 *   3. The four READS collapse to one, because they are one decision. Their group note
 *      already said "safe to leave on", and the individual switches are a debugging need
 *      rather than an operating one — one of them, Read from Shopify, is the switch that
 *      used to mark every pending order FAILED. Behind a disclosure it stays reachable
 *      without inviting the mistake.
 *   4. Intervals, API codes and the partner classification move to Advanced. The cadence is
 *      still stated on the main view as text, under the SAME label as the control — this
 *      screen was once two cards naming one setting twice under two different names, and
 *      that is the thing not to repeat.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Page, Card, Text, Box, BlockStack, InlineStack, Button,
  Select, Banner, Spinner, Checkbox, Divider, Tooltip, Collapsible, Link,
  Autocomplete, Icon,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useConnectorSettings, useUpdateConnectorSettings } from '../utils/queries';

const when = (d) => (d ? new Date(d).toLocaleString() : '—');

/**
 * The API codes an entry names, as links to where Mirakl publishes their limits.
 *
 * Every ceiling on this screen quotes Mirakl at an admin who otherwise has no way to check
 * it — and the external-partner floor asks them to make a decision on the strength of a
 * number whose source was not shown. The server supplies the URLs; codes it has no page for
 * (Shopify's read family, for instance) simply produce nothing.
 */
const DocLinks = ({ docs }) => (
  Array.isArray(docs) && docs.length > 0 ? (
    <InlineStack gap="200" wrap>
      {docs.map((d) => (
        <Link key={d.code} url={d.url} target="_blank" removeUnderline monochrome>
          <Text variant="bodySm" tone="subdued">{`${d.code} docs`}</Text>
        </Link>
      ))}
    </InlineStack>
  ) : null
);

/** Group ids the server sends. Reads collapse; writes stay individual. */
const isRead = (groupId) => groupId === 'readMirakl' || groupId === 'readShopify';

export default function SyncSettings() {
  const { data, isLoading, error } = useConnectorSettings();
  const save = useUpdateConnectorSettings();

  // Local draft so the whole form saves at once, as the design's single Save implies.
  const [draft, setDraft] = useState({});
  const [banner, setBanner] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  // Typeahead state for the timezone field. Empty means "show what is selected"; anything
  // typed filters the list and is what the field displays until a choice is made.
  const [tzQuery, setTzQuery] = useState('');

  // Reset the draft whenever the server's view changes — including after a save, where
  // the server may have adjusted what is selectable.
  useEffect(() => {
    if (!data) return;
    const next = {
      externalPartner: data.externalPartner.value,
      inventoryMode: data.inventory.value,
      overnightTimezone: data.overnight?.timezone.value,
      overnightHour: data.overnight?.hour.value,
    };
    for (const s of data.schedules) next[s.key] = s.value;
    // Toggles arrive grouped by direction; the draft is flat, keyed the same way the PUT is.
    for (const g of data.jobs ?? []) for (const t of g.toggles) next[t.key] = t.enabled;
    setDraft(next);
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return [];
    const changed = [];
    if (draft.externalPartner !== data.externalPartner.value) changed.push('externalPartner');
    if (data.overnight) {
      if (draft.overnightTimezone !== data.overnight.timezone.value) changed.push('overnightTimezone');
      // Compared as strings: the select hands back a string and the server sends one, so a
      // number here would report the hour as changed on every render.
      if (String(draft.overnightHour) !== String(data.overnight.hour.value)) changed.push('overnightHour');
    }
    if (draft.inventoryMode !== data.inventory.value) changed.push('inventoryMode');
    for (const s of data.schedules) if (draft[s.key] !== s.value) changed.push(s.key);
    for (const g of data.jobs ?? []) {
      for (const t of g.toggles) if (draft[t.key] !== t.enabled) changed.push(t.key);
    }
    return changed;
  }, [data, draft]);

  /** Every toggle, flat, tagged with the group it came from. */
  const all = useMemo(
    () => (data?.jobs ?? []).flatMap((g) => g.toggles.map((t) => ({ ...t, groupId: g.id }))),
    [data],
  );
  const reads = all.filter((t) => isRead(t.groupId));
  const writes = all.filter((t) => !isRead(t.groupId));
  const isOn = (t) => draft[t.key] ?? t.enabled;
  const readsOn = reads.filter(isOn).length;
  const writesOn = writes.filter(isOn).length;

  if (error) {
    return (
      <Page fullWidth>
        <Banner tone="critical">Unable to load connector settings: {error.message}</Banner>
      </Page>
    );
  }

  if (isLoading || !data) {
    return <Page fullWidth><Box padding="600"><Spinner /></Box></Page>;
  }

  const setMany = (entries) => setDraft((d) => ({ ...d, ...entries }));

  const onSave = async () => {
    setBanner(null);
    const patch = {};
    for (const k of dirty) patch[k] = draft[k];
    try {
      const res = await save.mutateAsync(patch);
      if (res.warning) {
        setBanner({ tone: 'warning', text: res.warning });
      } else if (res.rescheduled) {
        setBanner({
          tone: 'success',
          text: `Saved. ${res.rescheduled.applied} of ${res.rescheduled.total} schedules re-registered`
            + (res.rescheduled.failed ? ` — ${res.rescheduled.failed} failed, see server logs.` : '.'),
        });
      } else {
        setBanner({ tone: 'success', text: 'Saved. No running schedule needed changing.' });
      }
    } catch (err) {
      setBanner({
        tone: 'critical',
        text: err?.validation?.length ? err.validation.join(' · ') : (err?.message || 'Save failed'),
      });
    }
  };

  /**
   * The interval select for one operation, shown only in Advanced.
   *
   * `s` is the schedule the toggle named through `intervalKey`, so the toggle-to-interval
   * mapping still lives in JOB_TOGGLES on the server and is only consumed here.
   */
  const intervalSelect = (s) => (
    <BlockStack gap="050">
      <Select
        // Hidden, not removed: still read aloud, but a visible "How often" above a control
        // whose options are all cadences was a label for something already obvious.
        label="How often"
        labelHidden
        options={draftOptions(s)}
        value={draft[s.key] ?? s.value}
        onChange={(v) => setDraft((d) => ({ ...d, [s.key]: v }))}
      />
      {/* No caption. The ceiling was the reason an option is missing from the list, which
          the list already expresses by not offering it, and the doc link repeated the one on
          the switch's own row directly above. Both said something true and neither was worth
          four lines under every select. */}
    </BlockStack>
  );

  const tzOptions = data?.overnight?.timezone.options ?? [];
  const tzValue = draft.overnightTimezone ?? data?.overnight?.timezone.value ?? '';
  const tzLabel = tzOptions.find((o) => o.value === tzValue)?.label ?? tzValue;
  // Capped at 50: Autocomplete renders every option it is handed, and 419 of them makes the
  // popover the length of the page before anyone has typed anything.
  const tzMatches = (tzQuery
    ? tzOptions.filter((o) => o.label.toLowerCase().includes(tzQuery.toLowerCase()))
    : tzOptions
  ).slice(0, 50);

  /**
   * "2:00 AM EDT" for an hour in a zone. Mirrors hourLabel on the server.
   *
   * Duplicated deliberately. The server's copy is what the SAVED settings are described
   * with; this one recomputes the label as the admin moves the two selects, before anything
   * is saved. Without it the times only changed after a save, so the controls looked inert
   * — which is the one thing a setting must never look.
   */
  const localHourLabel = (h, tz) => {
    const suffix = h < 12 ? 'AM' : 'PM';
    const twelve = h % 12 === 0 ? 12 : h % 12;
    let abbrev = tz;
    try {
      abbrev = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value ?? tz;
    } catch {
      // An unknown zone typed into the field. The name itself is a worse label than an
      // abbreviation but still unambiguous, and the save is what refuses it.
    }
    return `${twelve}:00 ${suffix} ${abbrev}`;
  };

  /** The interval as the admin currently has it set, not as the server last saved it. */
  const draftIntervalLabel = (schedule, value) => {
    const option = schedule?.options?.find((o) => o.value === value);
    if (!option) return null;
    if (value !== 'daily') return option.label;
    const base = Number(draft.overnightHour ?? data.overnight?.hour.value ?? 2);
    const tz = draft.overnightTimezone ?? data.overnight?.timezone.value ?? 'UTC';
    const hour = (base + (schedule.dailyOffsetHours ?? 0)) % 24;
    return `Daily, around ${localHourLabel(hour, tz)}`;
  };

  /** A schedule's options, with any daily one relabelled from the draft. */
  const draftOptions = (schedule) => schedule.options.map((o) => ({
    label: (o.value === 'daily' ? draftIntervalLabel(schedule, 'daily') : o.label)
      + (o.disabled ? ' — unavailable' : ''),
    value: o.value,
    disabled: o.disabled,
  }));

  /**
   * The schedule a WRITE gets to set here, or null.
   *
   * A write owns its interval unless a read is paced by the same one — "Create Shopify
   * orders" runs off the order sweep, and that sweep belongs to "Fetch orders". Putting the
   * control on both would be two inputs writing one value, where changing either silently
   * moves the other.
   */
  const ownedSchedule = (t) => {
    if (!t.intervalKey) return null;
    const sharedWithARead = reads.some((r) => r.intervalKey === t.intervalKey);
    if (sharedWithARead) return null;
    return (data.schedules ?? []).find((x) => x.key === t.intervalKey) ?? null;
  };

  /**
   * The cadence as a sentence, for the main view. Same label as the control in Advanced.
   *
   * An operation with no configurable interval takes its sentence from the server, which is
   * the only place that knows what actually triggers it. This used to be one hardcoded line
   * saying everything ran with the order fetch; that is true of order acceptance and of
   * nothing else, so it told an admin that a credential check and the nightly Shopify mirror
   * ran on a sweep neither has anything to do with.
   */
  const cadenceLine = (t) => {
    if (!t.intervalKey) return t.cadence ?? null;
    const s = (data.schedules ?? []).find((x) => x.key === t.intervalKey);
    const chosen = draft[t.intervalKey] ?? s?.value;
    const label = draftIntervalLabel(s, chosen) ?? t.intervalLabel;
    // The interval alone. This used to prefix the schedule's own name — "Fetch orders —
    // Order sweep: Every hour" — which named the operation twice and named it the second
    // time in our word for it. The row already says what it is.
    return label ? `${label}.` : null;
  };

  /**
   * One WRITE, with what it does next to the switch.
   *
   * The line under each switch used to swap between `does` and `whenOff` depending on the
   * state, so ticking a box rewrote the sentence beneath it. It said one thing worth saying
   * — two of the three retailer writes cost money while off — but it said it by making the
   * row change under the cursor, and an unticked box already says the switch is off. The
   * consequence is still served on `whenOff` if it is ever wanted as a tooltip.
   */
  const writeRow = (t) => {
    const on = isOn(t);
    const isInventory = t.key === 'pushInventoryEnabled';
    /**
     * What the SELECTED mode does, in one sentence, served with the option it belongs to.
     *
     * Read from the draft rather than the saved value, so the line changes as the dropdown
     * changes — an explanation that only updates after Save would describe the mode you just
     * moved away from.
     */
    const modeHelp = isInventory
      ? (data.inventory.options.find(
        (o) => o.value === (draft.inventoryMode ?? data.inventory.value),
      )?.help ?? null)
      : null;
    return (
      <BlockStack gap="150" key={t.key}>
        <Checkbox
          label={t.label}
          checked={on}
          onChange={(v) => setDraft((d) => ({ ...d, [t.key]: v }))}
        />
        <Box paddingInlineStart="600">
          <BlockStack gap="200">
            <Text variant="bodySm" tone={on ? 'subdued' : 'caution'}>
              {/* Always what the switch DOES, never what happens while it is off. The text
                  swapping as you tick made the row rewrite itself under the cursor, and the
                  off state is already on screen — the box is unticked. `whenOff` is still
                  served, unrendered, if it is ever wanted as a tooltip. */}
              {t.does}
            </Text>

            {/* Stock is a MODE before it is an interval: it sends on change, and the
                interval only decides how often the queued changes go out in a batch. Two
                controls, so both are labelled — hiding the label works in Advanced where a
                lone select sits under its own switch, and stops working the moment there
                are two of them side by side. */}
            {isInventory && on && (
              <Box width="320px">
                <Select
                  label="When to send"
                  options={data.inventory.options.map((o) => ({
                    label: o.disabled ? `${o.label} — not built` : o.label,
                    value: o.value,
                    disabled: o.disabled,
                  }))}
                  value={draft.inventoryMode ?? data.inventory.value}
                  onChange={(v) => setDraft((d) => ({ ...d, inventoryMode: v }))}
                />
              </Box>
            )}

            {/* Outside the 320px Box on purpose. Inside it the sentence wraps to five lines
                in a narrow column; here it runs at the card's width, which is how every other
                explanatory line on this screen is set. */}
            {isInventory && on && modeHelp && (
              <Text variant="bodySm" tone="subdued">{modeHelp}</Text>
            )}

            {/* The interval itself, where this switch is the one that owns it. It was text
                on this screen and a control only in Advanced, which meant the one number an
                admin most wants to change was the one thing they could not reach. */}
            {on && ownedSchedule(t) && (
              <Box width="320px">
                {/* "How often" for both. It read "How often batches go out" on the stock
                    row, to distinguish it from the mode above — but "batches" is our word for
                    how the sending is implemented, and the two labels already ask visibly
                    different questions: when to send, and how often. */}
                <Select
                  label="How often"
                  options={draftOptions(ownedSchedule(t))}
                  value={draft[ownedSchedule(t).key] ?? ownedSchedule(t).value}
                  onChange={(v) => setDraft((d) => ({ ...d, [ownedSchedule(t).key]: v }))}
                />
              </Box>
            )}

            {/* Only where the server sends one. Most rows pace exactly what their label
                says and need no sentence saying so; these are the two where the interval
                means something other than what an admin reads into it. */}
            {on && ownedSchedule(t) && t.intervalHelp && (
              <Text variant="bodySm" tone="subdued">{t.intervalHelp}</Text>
            )}

            {/* Only when someone else owns the interval — then there is nothing to set here
                and the cadence is stated so the row is not silent about when it runs. */}
            {on && !ownedSchedule(t) && cadenceLine(t) && (
              <Text variant="bodySm" tone="subdued">{cadenceLine(t)}</Text>
            )}
          </BlockStack>
        </Box>
      </BlockStack>
    );
  };

  /** One READ, in Advanced: the switch, its API code, and its own interval. */
  const readRow = (t, claimed) => {
    const on = isOn(t);
    const schedule = t.intervalKey
      ? (data.schedules ?? []).find((s) => s.key === t.intervalKey)
      : null;
    const owns = Boolean(schedule) && !claimed.has(t.intervalKey);
    if (schedule && owns) claimed.set(t.intervalKey, t.label);
    const followsLabel = schedule && !owns ? claimed.get(t.intervalKey) : null;

    return (
      <BlockStack gap="150" key={t.key}>
        <InlineStack gap="200" blockAlign="center" wrap>
          <Checkbox
            label={t.label}
            checked={on}
            onChange={(v) => setDraft((d) => ({ ...d, [t.key]: v }))}
          />
          {/* No API code and no doc link. Which Mirakl endpoint a switch calls is a fact
              about our implementation, not about the decision in front of the admin — and
              the link went to a page written for whoever integrates it, not for whoever
              operates it. The one link kept is on the external-partner box, because that
              one asks the admin to act on a number they cannot otherwise check. */}
        </InlineStack>
        <Box paddingInlineStart="600">
          <BlockStack gap="200">
            {/* What it does, on or off — see the note on the write rows. */}
            <Text variant="bodySm" tone="subdued">{t.does}</Text>
            {schedule && owns && <Box width="320px">{intervalSelect(schedule)}</Box>}
            {followsLabel && (
              <Text variant="bodySm" tone="subdued">
                {/* No "above": in Advanced the owning row may be in a different card. */}
                Happens every time <b>{followsLabel}</b> runs.
              </Text>
            )}
            {/* No interval and no owning sweep: whatever does make it run, said plainly. */}
            {!schedule && !followsLabel && t.cadence && (
              <Text variant="bodySm" tone="subdued">{t.cadence}</Text>
            )}
            {/* The classification that sets the OR11 floor lives under the operation it
                constrains, not in a section of its own — it does nothing else. */}
            {t.intervalKey === 'orderSweepInterval' && owns && (
              <BlockStack gap="050">
                <Checkbox
                  label={data.externalPartner.label}
                  checked={draft.externalPartner ?? data.externalPartner.value}
                  onChange={(v) => setDraft((d) => ({ ...d, externalPartner: v }))}
                />
                <Box paddingInlineStart="600">
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">{data.externalPartner.note}</Text>
                    {data.externalPartner.howToTell && (
                      <Text variant="bodySm" tone="subdued">
                        {data.externalPartner.howToTell}
                      </Text>
                    )}
                    <DocLinks docs={data.externalPartner.docs} />
                  </BlockStack>
                </Box>
              </BlockStack>
            )}
          </BlockStack>
        </Box>
      </BlockStack>
    );
  };

  const readGroups = (data.jobs ?? []).filter((g) => isRead(g.id));
  const writeGroups = (data.jobs ?? []).filter((g) => !isRead(g.id));

  return (
    <Page fullWidth>
      <BlockStack gap="400">

        {/* STICKY, so Save is reachable from anywhere on the page.

            The page is long enough that changing a switch near the bottom scrolled the Save
            button out of sight, which reads as "there is no way to save this". Sticky keeps
            the unsaved count and the button together with the title.

            Plain CSS rather than Polaris ContextualSaveBar: that component is the more native
            pattern, but it renders through Frame context and would throw where this page is
            mounted without a Frame. The tokens below are Polaris custom properties, so the bar
            follows the theme instead of hardcoding a light background. */}
        <div
          style={{
            position: 'sticky',
            // BELOW the Frame's top bar, not at the viewport top. `top: 0` pinned the header
            // behind the fixed bar, so its title and buttons were hidden under it and only
            // the last line showed — it looked like sticky was half-working. --pg-top-bar-height
            // is Polaris' own 3.5rem, and --pc-frame-offset covers a Frame given an offset.
            top: 'calc(var(--pc-frame-offset, 0px) + var(--pg-top-bar-height, 3.5rem))',
            zIndex: 20,
            // The PAGE background, not the card surface. --p-color-bg-surface is the white
            // a Card is painted in, so against this page's grey it read as a white band
            // stretched across the top rather than as the page holding still.
            background: 'var(--p-color-bg)',
            paddingBlock: 'var(--p-space-300)',
            // A rule only once there is something to save. With nothing pending the header
            // is just the page title, and a permanent line under it drew a band of its own.
            borderBlockEnd: dirty.length > 0
              ? '1px solid var(--p-color-border-emphasis)'
              : '1px solid transparent',
          }}
        >
          <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
            <BlockStack gap="050">
              <Text variant="headingLg" as="h1">Connector Settings</Text>
              {/* One sentence, and only what is worth saying. A "Running" badge beside a
                  line already describing what is running said it twice, and "Not changing
                  anything" restated four unticked boxes visible directly below it. What
                  survives is the state that is NOT visible from the rest of the page:
                  nothing running at all, some reads off, or writes switched on. */}
              <Text variant="bodySm" tone="subdued">
                {readsOn === 0 && writesOn === 0
                  ? 'Nothing is running.'
                  : readsOn === 0
                    ? 'Not collecting any data.'
                    : readsOn === reads.length
                      ? 'Collecting data from retailers and Shopify.'
                      : `Collecting some data — ${readsOn} of ${reads.length} switched on.`}
                {writesOn > 0
                  ? ` Allowed to change ${writesOn} thing${writesOn === 1 ? '' : 's'}.`
                  : ''}
              </Text>
              {/* Provenance belongs with the state it describes. At the foot of the page it
                  was below Advanced, so on a screen where Advanced is collapsed it read as a
                  footnote to the disclosure rather than to the settings. */}
              <InlineStack gap="150" blockAlign="center">
                <Text variant="bodySm" tone="subdued">
                  Last changed {when(data.updatedAt)}{data.updatedBy ? ` by ${data.updatedBy}` : ''}.
                </Text>
                <Tooltip content="Changes take effect as soon as you save. Nothing needs restarting.">
                  <Text variant="bodySm" tone="subdued">ⓘ</Text>
                </Tooltip>
              </InlineStack>
            </BlockStack>
            <InlineStack gap="200" blockAlign="center">
              {/* Pause everything / Resume reading removed.
                  It did not pause — it destroyed. Resume did not restore what was on, it set
                  reads on and writes off unconditionally, so an admin running with two writes
                  enabled lost that configuration by pressing the pair and had to remember
                  what had been there. A control that cannot undo itself is not a pause.
                  Each switch is on this screen already, and "stop this retailer now" lives on
                  the retailer's own row, where it belongs. */}
              {dirty.length > 0 && (
                <Text variant="bodySm" tone="caution">
                  {dirty.length} unsaved change{dirty.length === 1 ? '' : 's'}
                </Text>
              )}
              {dirty.length > 0 && (
                <Button
                  disabled={save.isPending}
                  onClick={() => {
                    // Back to the server's values, field by field, rather than a page reload:
                    // a reload would also drop the save result banner the admin is reading.
                    const next = {
                      externalPartner: data.externalPartner.value,
                      inventoryMode: data.inventory.value,
                      overnightTimezone: data.overnight?.timezone.value,
      overnightHour: data.overnight?.hour.value,
                    };
                    for (const s of data.schedules) next[s.key] = s.value;
                    for (const g of data.jobs ?? []) for (const t of g.toggles) next[t.key] = t.enabled;
                    setDraft(next);
                  }}
                >
                  Discard
                </Button>
              )}
              <Button
                variant="primary"
                disabled={dirty.length === 0 || save.isPending}
                loading={save.isPending}
                onClick={onSave}
              >
                Save
              </Button>
            </InlineStack>
          </InlineStack>
        </div>

        {banner && (
          <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.text}</Banner>
        )}

        {/* The state card that used to sit here is gone. It held one line of state, one
            button, and a paragraph explaining what the button did — all of it above the
            settings an admin came to change, and none of it visible once they scrolled. The
            state and the button are in the sticky header now; what Pause does is on the
            button itself, where it is read at the moment it matters. */}

        {/* THE WRITES, individually, because each costs something different when off. */}
        {writeGroups.length > 0 && (
          <Card>
            {/* No card heading. "What 2W is allowed to change" sat above a group headed
                "Writes to Mirakl" above a switch called "Push tracking" — three levels of
                naming for one decision, and the paragraph under it repeated what each
                switch's own consequence line already says. The group heading survives only
                where it groups more than one switch. */}
            <BlockStack gap="400">
              {writeGroups.map((group, i) => (
                <BlockStack gap="300" key={group.id}>
                  {i > 0 && <Divider />}
                  {/* No heading and no group note. Each switch names where it writes —
                      "Push tracking to Mirakl" — so a heading saying "Writes to Mirakl"
                      above three of them was the destination stated four times, and the
                      note under it repeated each switch's own consequence line. The divider
                      still separates the two destinations. */}
                  <BlockStack gap="400">{group.toggles.map(writeRow)}</BlockStack>
                </BlockStack>
              ))}
            </BlockStack>
          </Card>
        )}

        {/* THE READS, as one decision. */}
        {reads.length > 0 && (
          <Card>
            {/* Heading dropped: "Keeping data up to date" above a switch labelled "Keep
                retailer and Shopify data up to date" is the same sentence twice. */}
            <BlockStack gap="300">
              <Checkbox
                label="Keep retailer and Shopify data up to date"
                // Honest about a mixed state rather than rounding it to on or off: the
                // individual switches are still reachable in Advanced, and someone who has
                // turned one off should not see this read as fully on.
                checked={readsOn === reads.length ? true : (readsOn === 0 ? false : 'indeterminate')}
                onChange={(v) => setMany(Object.fromEntries(reads.map((t) => [t.key, v])))}
                helpText={readsOn > 0 && readsOn < reads.length
                  ? `${readsOn} of ${reads.length} on — see Advanced for which.`
                  : undefined}
              />

              {readsOn > 0 && (
                <Box paddingInlineStart="600">
                  <BlockStack gap="050">
                    {reads.filter(isOn).map((t) => (
                      <Text variant="bodySm" tone="subdued" key={t.key}>
                        {t.label} — {cadenceLine(t) || 'runs on its own schedule.'}
                      </Text>
                    ))}
                  </BlockStack>
                </Box>
              )}
            </BlockStack>
          </Card>
        )}

        {/* ADVANCED — the per-read switches, every interval, the API codes and the partner
            classification. Named for what is inside rather than "More", so it is findable. */}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text variant="headingMd" as="h2">Advanced</Text>
                <Text variant="bodySm" tone="subdued">
                  Individual read switches and how often each one runs.
                </Text>
              </BlockStack>
              <Button
                onClick={() => setAdvanced((v) => !v)}
                ariaExpanded={advanced}
                ariaControls="connector-advanced"
                disclosure={advanced ? 'up' : 'down'}
              >
                {advanced ? 'Hide' : 'Show'}
              </Button>
            </InlineStack>

            <Collapsible open={advanced} id="connector-advanced">
              <BlockStack gap="400">
                {/* The per-brand caveat comes first, because it frames every control below
                    it. The amber banner that used to sit here said each interval is capped
                    by Mirakl — which every select already says underneath itself, in the
                    specific number that applies to it rather than in the abstract. */}
                <Banner tone="info">
                  <Text variant="bodySm">
                    <strong>Intervals are system-wide, not per-brand.</strong> Mirakl&rsquo;s
                    call-frequency budget belongs to the retailer account, so every brand
                    sharing one account shares one ceiling. Set once here and every
                    brand-to-retailer connection uses it — which is why there is no interval
                    field on an individual connection.
                  </Text>
                </Banner>

                {/* Two fields, because they are two decisions: what time the overnight work
                    starts, and the clock that time is read on. Every time on this screen is
                    shown in the zone chosen here. */}
                {data.overnight && (
                  <BlockStack gap="200">
                  <InlineStack gap="300" blockAlign="end" wrap>
                    <Box width="200px">
                      <Select
                        label={data.overnight.hour.label}
                        options={data.overnight.hour.options}
                        value={String(draft.overnightHour ?? data.overnight.hour.value)}
                        onChange={(v) => setDraft((d) => ({ ...d, overnightHour: v }))}
                      />
                    </Box>
                    <Box width="360px">
                      {/* A field that suggests, not a list that restricts. The server sends
                          every zone this build knows — around 400 — so a select would be
                          unusable and a shortlist of eight would exclude whoever is not in
                          one of them. Typing filters; anything typed is accepted and the
                          save is what decides whether it is a real zone. */}
                      <Autocomplete
                        options={tzMatches}
                        selected={[tzValue]}
                        onSelect={([v]) => { if (v) { setTzQuery(''); setDraft((d) => ({ ...d, overnightTimezone: v })); } }}
                        textField={(
                          <Autocomplete.TextField
                            label={data.overnight.timezone.label}
                            value={tzQuery || tzLabel}
                            onChange={setTzQuery}
                            onFocus={() => setTzQuery('')}
                            prefix={<Icon source={SearchIcon} />}
                            placeholder="Search or type a timezone"
                            autoComplete="off"
                          />
                        )}
                      />
                    </Box>
                  </InlineStack>

                  {/* Full width, under both controls rather than squeezed beside one — as a
                      column beside a 200px select it ran to fifteen lines and pushed the
                      timezone field off the fold. Both sentences are on the page rather than
                      behind a hover: an admin choosing this time needs to know it moves four
                      jobs, and why they cannot be set apart, before they change it — not
                      after wondering whether it worked. */}
                  {data.overnight.hour.help && (
                    <BlockStack gap="050">
                      <Text variant="bodySm" tone="subdued">{data.overnight.hour.help}</Text>
                      {data.overnight.hour.why && (
                        <Text variant="bodySm" tone="subdued">{data.overnight.hour.why}</Text>
                      )}
                    </BlockStack>
                  )}
                  </BlockStack>
                )}

                {(() => {
                  // Carried across groups so the first operation to claim a shared interval
                  // owns its control and the second only says whose cadence it follows.
                  const claimed = new Map();
                  return readGroups.map((group, i) => (
                    <BlockStack gap="300" key={group.id}>
                      {i > 0 && <Divider />}
                      {/* A group holding one switch is a heading, a note and a switch all
                          saying the same thing — "Reads from Shopify" above "Read from
                          Shopify". The switch carries its own label and consequence, so the
                          heading only earns its place when it groups more than one. */}
                      {group.toggles.length > 1 && (
                        <BlockStack gap="050">
                          <Text variant="bodySm" fontWeight="semibold">{group.label}</Text>
                          <Text variant="bodySm" tone="subdued">{group.note}</Text>
                        </BlockStack>
                      )}
                      <BlockStack gap="400">
                        {group.toggles.map((t) => readRow(t, claimed))}
                      </BlockStack>
                    </BlockStack>
                  ));
                })()}
              </BlockStack>
            </Collapsible>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
